import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { withForbidden } from '../../../../../global/components/HOCs/with-forbidden';
import { withLayout } from '../../../../../global/components/HOCs/with-layout';
import { withScrollTop } from '../../../../../global/components/HOCs/with-scroll-Top';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { DemographicsContext } from '../../../../../global/models/demographics-model';
import { TcsmFeatures } from '../../../../../global/models/features-model';
import { TcsmEpiApiState, TcsmStates } from '../../../../../root/tcsm-model';
import AccountInfoContainer from '../../../../account-info/components/account-info-container';
import { AccountInfoRoutes } from '../../../../account-info/constants/routes';
import { DependentInfo } from '../../../../account-info/features/dependents/dependent-info';
import { MaritalStatusApp } from '../../../../account-info/features/marital-status/marital-status';
import { PersonalInformationApp } from '../../../../account-info/features/personal-info/personal-info';
import { MyRelationshipsApp } from '../../../../account-info/features/relationships/my-relationships';
import { useAccountInfoSettings } from '../../../../account-info/hooks/useAccountInfoSettings';
import { useDemographicsPerson } from '../../../../account-info/hooks/useDemographicsPerson';
import { useSpouseInformation } from '../../../../account-info/hooks/useSpouseInfo';
import { TcsmAppConfigs } from '../../../../../global/models/configuration-models';
import { ParticipantsEnrollmentRoutes } from '../models/routes';
import { AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../../annual-enrollment/models/annual-enrollment-models';
import { NewHireMemberEnrollmentPersonalInfoWizard } from '../../new-hire-enrollment/member/wizards/new-hire-member-enrollment-wizard';
import GenericStepper from '../../../../../global/components/navigation/generic-stepper';
import { useBenefitEnrollmentStep } from '../../new-hire-enrollment/hooks/useBenefitEnrollmentStep';
import { setCurrentNewHireBenefitEnrollmentStep, setCurrentNewHireEnrollmentStep } from '../../new-hire-enrollment/slice/new-hire-enrollment-slices';
import { getBenefitEnrollmentSteps, getBenefitEnrollmentStepItems, GetCustomCoverageModel } from './helper/benefit-enrollment-helper';
import { apiErrorClear } from '../../../../../global/ducks/base-api';
import { NewHireEnrollmentStep } from '../../new-hire-enrollment/models/new-hire-enrollment-models';
import { CoverageEnrollment } from '../../new-hire-enrollment/enrollment/coverage/coverage-enrollment';
import { useBillingAccountNumber, useBillingAccountOwnerClientNumber } from '../hooks/useGroupId';
import { ConfirmationStatusCode } from '../../new-hire-enrollment/models/new-hire-confirmation-models';


const RelationshipsWrapper = withScrollTop()(withLayout(null, GpsType.NEW_PARTICIPANT_RELATIONSHIP)(withForbidden(TcsmFeatures.Relationships)(AccountInfoContainer)));
const PersonalInfoWrapper = withScrollTop()(withLayout(null, GpsType.NEW_PARTICIPANT_BENEFIT_PERSON_INFO)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer)));
const MaritalStatusWrapper = withLayout(null, GpsType.MARITAL_STATUS)(withForbidden(TcsmFeatures.MaritalStatus)(AccountInfoContainer));
const DependentsWrapper = withLayout(null, GpsType.ADD_DEPENDENT)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer));

interface Props {
	settings: TcsmAppConfigs
}

function BenefitEnrollment(props: Props) {

	const { settings } = props;
	const dispatch = useDispatch();
	const app = useContext(DemographicsContext);
	const routeMatch = useRouteMatch();
	const demographicsSettingsLoaded = useAccountInfoSettings(settings.personalInfoSettingId, settings.relationshipsSettingId);
	const personGuid = useSelector<TcsmStates, string>(state => state.personalInfo[app]?.currentPerson?.person_guid);
	const personLoaded = useDemographicsPerson(app);
	const spousesLoaded = useSpouseInformation(personGuid);
	const epiApiState = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const history = useHistory();

	useEffect(() => {
		dispatch(apiErrorClear());
		dispatch(setCurrentNewHireEnrollmentStep(NewHireEnrollmentStep.BENEFIT_ENROLLMENT));

	}, []);


	if (!personLoaded || !spousesLoaded || !demographicsSettingsLoaded) {
		return <LoadingCircle />;
	}

	const wizard = new NewHireMemberEnrollmentPersonalInfoWizard(epiApiState, AnnualEnrollmentKey.newHireEnrollment, app, dispatch, history);

	return (
		<AnnualEnrollmentContext.Provider value={AnnualEnrollmentKey.participantEnrollment}>
			<GenericStepper
				basePath={routeMatch.path}
				allSteps={getBenefitEnrollmentSteps()}
				useCurrentStep={useBenefitEnrollmentStep}
				setCurrentStep={setCurrentNewHireBenefitEnrollmentStep}
				progressItems={getBenefitEnrollmentStepItems(epiApiState?.newHireSettingsState?.epiContent)}
			/>
			<Switch>
				<Route path={`${routeMatch.path}${AccountInfoRoutes.PersonalInfoSegment}`} render={() => (
					<PersonalInfoWrapper basePath={routeMatch.path} hideBreadcrumbs>
						<PersonalInformationApp basePath={routeMatch.path} customPersonalInfoWizard={wizard} />
					</PersonalInfoWrapper>
				)} />
				<Route exact path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} render={() => (
					<RelationshipsWrapper basePath={routeMatch.path} hideBreadcrumbs>
						<MyRelationshipsApp basePath={routeMatch.path} />
					</RelationshipsWrapper>
				)} />
				<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.MaritalStatusSegment}`} render={() => (
					<MaritalStatusWrapper basePath={routeMatch.path} hideBreadcrumbs>
						<MaritalStatusApp basePath={routeMatch.path} />
					</MaritalStatusWrapper>
				)} />
				<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.DependentSegment}`} render={() => (
					<DependentsWrapper basePath={routeMatch.path} hideBreadcrumbs>
						<DependentInfo relationshipsPageRoute={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} />
					</DependentsWrapper>
				)} />
				<Route path={`${routeMatch.path}${ParticipantsEnrollmentRoutes.Coverage}`} render={() => (
					<CoverageEnrollment
						basePath={routeMatch.path}
						queryString='account_number'
						getEmployerIdHook={useBillingAccountNumber}
						isParticipant
						customContentModel={GetCustomCoverageModel(epiApiState?.participantEnrollmentSettingsState?.epiContent)}
						eventType='new_participant'
						getBillingAccountOwnerCn={useBillingAccountOwnerClientNumber}
						confirmationCode={ConfirmationStatusCode.NEW_PARTICIPANT}
					/>
				)} />

			</Switch>
		</AnnualEnrollmentContext.Provider >

	);
}

export default BenefitEnrollment;